import { weekDays, getWeekNumber, copyDate, dateToString, months } from '../../../utilities/calendar-utilities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { ChangeCalendarView } from './Calendar';
import { Trans } from 'react-i18next';
import CoreComponent from 'utilities/component-utilities/CoreComponent';
import React from 'react';
import { DateUtil } from 'utilities/date-utilities';

// #region model definition
interface CalendarDayProps {
    showWeekNumber?: boolean,
    isDisabledDate: (date: Date) => boolean,
    viewYear: number,
    viewMonth: number,
    onChange: (date: Date, e: React.MouseEvent) => void,
    value: Date | null,
    changeView: (view: ChangeCalendarView, e?: React.MouseEvent | WheelEvent) => void,
    highlightDates?: Array<Date> | null,
    name: string
}
interface CalendarDayState { }
// #endregion model definition
class CalendarDay extends CoreComponent<CalendarDayProps, CalendarDayState> {
    readonly today: Date;
    readonly calendarContentRef: React.RefObject<HTMLDivElement | null>;
    private onWheelEvent: any;

    constructor(props: CalendarDayProps) {
        super(props);
        this.today = new Date();
        this.calendarContentRef = React.createRef<HTMLDivElement | null>();
        this.state = {
        };
    }

    componentWillUnmount() {
        this.mouseLeave();
    }

    private get firstDateOfThisMonth() {
        return new Date(this.props.viewYear, this.props.viewMonth, 1);
    }

    private get lastDayOfThisMonth() {
        return new Date(this.props.viewYear, this.props.viewMonth + 1, 0);
    }

    private goToToday = (e: React.MouseEvent) => {
        this.props.changeView({
            month: this.today.getMonth(),
            year: this.today.getFullYear(),
            view: 'date'
        }, e);
    }

    private moveMonth = (moveFor: number, e: React.MouseEvent | WheelEvent) => {
        let month = this.props.viewMonth + moveFor;
        let year = this.props.viewYear + 0;
        if (month === -1) {
            month = 11;
            year--;
        }
        if (month === 12) {
            month = 0;
            year++;
        }

        this.props.changeView({
            month: month,
            year: year
        }, e);
    }

    private onDayClick = (date: Date, e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (this.props.isDisabledDate(date)) return;
        this.props.onChange(date, e);
    }

    private mouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
        this.onWheelEvent = this.calendarContentRef.current?.addEventListener('wheel', e => {
            e.preventDefault();
            e.stopPropagation();
            if (e.deltaY > 0) {
                this.moveMonth(1, e);
            } else if (e.deltaY < 0) {
                this.moveMonth(-1, e);
            }
        });
    }

    private mouseLeave = () => {
        this.calendarContentRef.current?.removeEventListener('wheel', this.onWheelEvent);
    }

    // #region render

    private renderWeekNumberHeader = () => {
        if (this.props.showWeekNumber) return <th key="week-number-header" className='week-number'>#</th>;
        else return <></>;
    }

    private renderDayOnCalendar = (date: Date) => {
        const day = copyDate(date);
        const dayString = dateToString(day);
        let className = weekDays[date.getDay()];
        if (dayString < dateToString(this.firstDateOfThisMonth) || dayString > dateToString(this.lastDayOfThisMonth)) {
            className += " not-in-month";
        }

        if (dayString === dateToString(this.today)) {
            className += ' today';
        }

        if (this.props.value && dayString === dateToString(this.props.value)) {
            className += ' selected';
        }

        if (this.props.isDisabledDate(date)) className += ' disabled';

        if (this.props.highlightDates?.some(x => DateUtil.compareOnAsIsoDate(x, date))) className += ' highlight';

        return (
            <td key={this.props.name + dayString}>
                <button
                    type="button"
                    name={`${this.props.name}-calendar-day-${dayString}`}
                    className={className}
                    onClick={e => this.onDayClick(day, e)}
                >
                    <span className={`${day.getDate() < 10 ? 'single-digits' : ''}`}>{day.getDate()}</span>
                </button>
            </td>
        );
    }

    private renderDateBody = () => {
        let dayOfWeek = this.firstDateOfThisMonth.getDay();
        dayOfWeek = (dayOfWeek === 0 ? 7 : dayOfWeek) - 2;
        let firstDayOfCalendar = new Date(new Date(this.firstDateOfThisMonth.setDate(dayOfWeek * -1)));
        const calendarBody: React.ReactNode[] = [];

        for (let calendarRowIndex = 0; calendarRowIndex < 6; calendarRowIndex++) {
            const calendarColumns = [];
            if (this.props.showWeekNumber) {
                const weekNumber = getWeekNumber(firstDayOfCalendar);
                calendarColumns.push(
                    <td key={`week-number-header-${weekNumber}`}>
                        <span className='week-number'>{weekNumber}</span>
                    </td>
                );
            }
            for (let calendarColumnIndex = 0; calendarColumnIndex < 7; calendarColumnIndex++) {
                calendarColumns.push(this.renderDayOnCalendar(firstDayOfCalendar));
                firstDayOfCalendar.setDate(firstDayOfCalendar.getDate() + 1);
            }

            calendarBody.push(
                <tr key={`${this.props.name}-calendar-table-row-${calendarRowIndex}`}>
                    {calendarColumns}
                </tr>
            );
        }

        return calendarBody;
    }

    render() {
        return (
            <>
                <header className="calendar-header"><div className='flex gap-4 text-4xl float-right'>
                    <button aria-label='up' type='button' onClick={e => this.moveMonth(-1, e)} ><FontAwesomeIcon icon={faCaretUp} /></button>
                    <button aria-label='down' type='button' onClick={e => this.moveMonth(1, e)} ><FontAwesomeIcon icon={faCaretDown} /></button>
                </div>
                    <div className='pt-2 pb-2'>
                        <button type='button' onClick={e => this.props.changeView('month', e)} >
                            <Trans i18nKey={`calendar.months.${months[this.props.viewMonth]}.name`} />
                            &nbsp;
                            {this.props.viewYear}
                        </button>
                        &nbsp;
                        &nbsp;
                        &nbsp;
                        &nbsp;
                        <button type='button' onClick={e => this.goToToday(e)} >{this.t('calendar.today')}</button>
                    </div>
                </header>
                <div ref={this.calendarContentRef} className="calendar-content" onMouseEnter={this.mouseEnter} onMouseLeave={this.mouseLeave}>
                    <table className="date-view">
                        <thead>
                            <tr key={`${this.props.name}-calendar-table-header`}>
                                {this.renderWeekNumberHeader()}
                                <th key={`${this.props.name}-calendar-table-header-monday`}>{this.t('calendar.weeks.monday.shot')}</th>
                                <th key={`${this.props.name}-calendar-table-header-tuesday`}>{this.t('calendar.weeks.tuesday.shot')}</th>
                                <th key={`${this.props.name}-calendar-table-header-wednesday`}>{this.t('calendar.weeks.wednesday.shot')}</th>
                                <th key={`${this.props.name}-calendar-table-header-thursday`}>{this.t('calendar.weeks.thursday.shot')}</th>
                                <th key={`${this.props.name}-calendar-table-header-friday`}>{this.t('calendar.weeks.friday.shot')}</th>
                                <th key={`${this.props.name}-calendar-table-header-saturday`}>{this.t('calendar.weeks.saturday.shot')}</th>
                                <th key={`${this.props.name}-calendar-table-header-sunday`}>{this.t('calendar.weeks.sunday.shot')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {this.renderDateBody()}
                        </tbody>
                    </table>
                </div>
            </>
        );
    }
    // #endregion render
}
export default CalendarDay;
